import React from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Search,
  Database,
  FlaskConical,
  Shield,
  FileText,
} from "lucide-react";
import styles from "./Features.module.css";

const features = [
  {
    icon: Brain,
    title: "Structured Case Comprehension",
    description:
      "Reads clinical notes, genetic reports, and lab results — extracting every feature and identifying gaps in the referring clinician's reasoning before retrieving a single paper.",
  },
  {
    icon: Search,
    title: "Autonomous Literature Retrieval",
    description:
      "Generates targeted paper titles, retrieves full text, and synthesises findings across dozens of publications, without being told what to search for.",
  },
  {
    icon: Database,
    title: "Structured Database Querying",
    description:
      "Simultaneously queries curated biomedical databases covering variant pathogenicity, disease associations, protein structure, and molecular mechanism, integrating structured evidence alongside the literature in a single pass.",
  },
  {
    icon: FlaskConical,
    title: "Ranked Hypothesis Formation",
    description:
      "Produces ranked hypotheses with explicit confidence levels: HIGH, MEDIUM, LOW, grounded in retrieved evidence. Holds multiple hypotheses simultaneously when the data supports it.",
  },
  {
    icon: Shield,
    title: "Adversarial Self-Testing",
    description:
      "Actively tries to refute each hypothesis using the same evidence. Flags where conclusions are weakened by missing data. Distinguishes pattern matching from genuine mechanistic reasoning.",
  },
  {
    icon: FileText,
    title: "Confidence-Labelled Brief",
    description:
      "Delivers a structured brief with every claim labelled as Established, Inferred, or Hypothetical, with citations, testable predictions, and prioritised next investigations.",
  },
];

export default function Features() {
  return (
    <section id="features" className={styles.section}>
      <div className={styles.background}>
        <div className={styles.glow} />
      </div>
      <div className={styles.container}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={styles.header}
        >
          <span className={styles.label}>How It Works</span>
          <h2 className={styles.title}>
            Six Phases. One{" "}
            <span className={styles.titleHighlight}>Evidence-Grounded</span>{" "}
            Brief.
          </h2>
          <p className={styles.subtitle}>
            MitoAgent doesn't just answer questions. It runs a structured
            reasoning pipeline — from case comprehension through literature
            retrieval, database querying, hypothesis formation, and adversarial
            testing — before producing a confidence-labelled brief ready for
            clinical or research use.
          </p>
        </motion.div>
        <div className={styles.grid}>
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className={styles.card}>
                <div className={styles.iconWrapper}>
                  <div className={styles.phaseNumber}>0{i + 1}</div>
                </div>
                <h3 className={styles.cardTitle}>{feature.title}</h3>
                <p className={styles.cardDescription}>{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
